import { useQuery } from "@tanstack/react-query";
import type { Workspace } from "../shared/schema";
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}
export async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...options?.headers },
  }).catch(() => {
    throw new ApiError("Workroom could not be reached. Check your connection and try again.", 0);
  });
  const text = await response.text();
  let result: unknown;
  try {
    result = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(
      `Workroom returned an unreadable response (${response.status}). Please reconnect and try again.`,
      response.status,
    );
  }
  if (!response.ok) {
    const data = (result && typeof result === "object" ? result : {}) as {
      error?: string;
      message?: string;
      requestId?: string;
    };
    throw new ApiError(
      `${data.error ?? data.message ?? "Request failed"}${data.requestId ? ` (reference ${data.requestId})` : ""}`,
      response.status,
    );
  }
  return result as T;
}
export function useWorkspace() {
  return useQuery({
    queryKey: ["workspace"],
    queryFn: () => api<Workspace>("/workspace"),
    refetchInterval: (query) =>
      query.state.error instanceof ApiError && [401, 403, 428].includes(query.state.error.status)
        ? false
        : 5000,
    retry: (count, error) =>
      !(error instanceof ApiError && [401, 403, 428].includes(error.status)) && count < 2,
  });
}
